import { Box, Stack, Typography, alpha } from "@mui/material";
import { CommunicationCard } from "./communication-card";
import { CountPill, Dot } from "./timeline-parts";

import type { TimelineGroup, TimelineRow } from "../program-timeline";
import type { FC, ReactNode } from "react";

/**
 * A group's rail: a stop for each timing, with a dot on a line in the group's colour, and its
 * Communications. The day of a person's date has a larger, filled dot.
 */
export const GroupTimeline: FC<{
  group: TimelineGroup;
  folderOf: (row: TimelineRow) => string | null;
  actionsOf: (row: TimelineRow) => ReactNode;
}> = ({ group, folderOf, actionsOf }) => {
  const last = group.stops.length - 1;
  const ring = alpha(group.color, 0.2);

  return (
    <Box component="section" aria-label={group.name}>
      <Stack
        direction="row"
        useFlexGap
        sx={{ alignItems: "center", flexWrap: "wrap", gap: 1.25, pt: 2, pb: 1 }}
      >
        <Dot color={group.color} size={12} />
        <Typography component="h3" sx={{ fontSize: 16, fontWeight: 700, lineHeight: "24px" }}>
          {group.name}
        </Typography>
        {group.code ? (
          <Box
            component="code"
            sx={{
              fontFamily: "monospace",
              fontSize: 12,
              lineHeight: "18px",
              px: 1,
              py: 0.25,
              border: 1,
              borderColor: "divider",
              borderRadius: "4px",
              bgcolor: "background.paper",
            }}
          >
            {group.code}
          </Box>
        ) : null}
        <Box sx={{ flex: 1 }} />
        <CountPill count={group.count} />
      </Stack>
      <Box
        component="ol"
        aria-label={`${group.name} timeline`}
        sx={{ listStyle: "none", m: 0, pt: 0.5, pl: 0.5, pr: 0, pb: 0 }}
      >
        {group.stops.map((stop, i) => (
          <Box component="li" key={stop.key} sx={{ display: "flex", gap: 1.5 }}>
            <Box aria-hidden sx={{ position: "relative", width: 20, flexShrink: 0 }}>
              {last > 0 ? (
                <Box
                  sx={{
                    position: "absolute",
                    left: 9,
                    width: 2,
                    bgcolor: ring,
                    ...(i === 0
                      ? { top: 10, bottom: 0 }
                      : i === last
                        ? { top: 0, height: 10 }
                        : { top: 0, bottom: 0 }),
                  }}
                />
              ) : null}
              <Box
                sx={{
                  position: "absolute",
                  top: 6,
                  left: stop.anchor ? 3 : 5,
                  width: stop.anchor ? 14 : 10,
                  height: stop.anchor ? 14 : 10,
                  borderRadius: "50%",
                  boxSizing: "border-box",
                  ...(stop.anchor
                    ? { bgcolor: group.color, boxShadow: `0 0 0 4px ${ring}` }
                    : { bgcolor: "background.paper", border: `2px solid ${group.color}` }),
                }}
              />
            </Box>
            <Stack spacing={1} sx={{ flex: 1, minWidth: 0, pb: 2.5 }}>
              <Stack
                direction="row"
                useFlexGap
                sx={{
                  alignItems: "center",
                  flexWrap: "wrap",
                  columnGap: 1,
                  rowGap: 0.5,
                  minHeight: 28,
                }}
              >
                <Typography
                  component="h4"
                  sx={{
                    fontSize: 14,
                    fontWeight: 600,
                    lineHeight: "22px",
                    color: stop.anchor ? group.color : "text.primary",
                  }}
                >
                  {stop.title}
                </Typography>
                <Typography variant="caption" sx={{ lineHeight: "18px", color: "text.secondary" }}>
                  {stop.detail}
                </Typography>
              </Stack>
              {stop.rows.map((row) => (
                <CommunicationCard
                  key={row.id}
                  row={row}
                  folder={folderOf(row)}
                  actions={actionsOf(row)}
                />
              ))}
            </Stack>
          </Box>
        ))}
      </Box>
    </Box>
  );
};
